import type { ReactNode } from "react";
import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate } from "react-router";
import { Globe, ImageIcon, Link as LinkIcon } from "lucide-react";

import { CenteredLoader, EmptyCard, ExternalLinkText, PageIntro } from "@/components/common";
import { getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { trpc } from "@/lib/trpc";
import { Badge } from "@runcheetah/ui";

type SourceMaterialRow = {
  id: string;
  name: string;
  subtitle: string;
  type: "website" | "social";
  typeLabel: string;
  href: string | null;
  addedAt: string | null;
  lastScannedAt: string | null;
  icon: ReactNode;
};

const formatDate = (value: string | null): string => {
  if (!value) return "-";

  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getHostLabel = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

export default function BrandSourceMaterialsPage() {
  const intl = useIntl();
  const { brand, brandQuery } = useRouteBrand();

  const instagramQuery = trpc.socialConnection.getInstagramImport.useQuery(
    { brandId: brand?.id ?? "", limit: 1 },
    { enabled: Boolean(brand?.id), refetchOnWindowFocus: true },
  );
  const galleryQuery = trpc.mediaGallery.list.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), refetchOnWindowFocus: true },
  );

  const rows = useMemo<SourceMaterialRow[]>(() => {
    if (!brand) return [];

    const websiteSources =
      galleryQuery.data
        ?.flatMap((asset) => asset.sources)
        .filter((source) => source.type === "website") ?? [];
    const websiteCreatedAt = websiteSources
      .map((source) => source.createdAt)
      .toSorted((left, right) => left.localeCompare(right));

    const nextRows: SourceMaterialRow[] = [
      {
        id: `website-${brand.id}`,
        name: getHostLabel(brand.sourceUrl),
        subtitle: brand.sourceUrl,
        type: "website",
        typeLabel: intl.formatMessage({
          id: "brandSourceMaterials.type.website",
          defaultMessage: "Website",
        }),
        href: brand.sourceUrl,
        addedAt: websiteCreatedAt[0] ?? null,
        lastScannedAt: websiteCreatedAt.at(-1) ?? null,
        icon: <Globe className="h-4 w-4" />,
      },
    ];

    const connection = instagramQuery.data?.connection ?? null;
    if (connection) {
      const sync = instagramQuery.data?.sync ?? null;
      const media = instagramQuery.data?.media ?? [];
      const permalink = media[0]?.permalink ?? null;
      nextRows.push({
        id: connection.id,
        name: connection.accountName
          ? `@${connection.accountName}`
          : intl.formatMessage({
              id: "brandSourceMaterials.instagram",
              defaultMessage: "Instagram",
            }),
        subtitle:
          permalink ??
          intl.formatMessage({
            id: "brandSourceMaterials.connectedInstagramAccount",
            defaultMessage: "Connected Instagram account",
          }),
        type: "social",
        typeLabel: intl.formatMessage({
          id: "brandSourceMaterials.type.social",
          defaultMessage: "Social",
        }),
        href: permalink,
        addedAt: connection.createdAt,
        lastScannedAt: sync?.lastSyncedAt ?? null,
        icon: <ImageIcon className="h-4 w-4" />,
      });
    }

    return nextRows;
  }, [brand, galleryQuery.data, instagramQuery.data, intl]);

  if (brandQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "brandSourceMaterials.loading",
          defaultMessage: "Loading source materials...",
        })}
      />
    );
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  if (galleryQuery.isPending || instagramQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "brandSourceMaterials.loading",
          defaultMessage: "Loading source materials...",
        })}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <PageIntro
        title={intl.formatMessage({
          id: "brandSourceMaterials.title",
          defaultMessage: "Source Materials",
        })}
        titleClassName="text-balance"
      />

      {rows.length === 0 ? (
        <EmptyCard icon={<LinkIcon className="h-8 w-8" />} contentClassName="h-44">
          <FormattedMessage
            id="brandSourceMaterials.empty"
            defaultMessage="No source materials are available yet."
          />
        </EmptyCard>
      ) : (
        <>
          <div className="grid gap-4 md:hidden">
            {rows.map((row) => (
              <div
                key={row.id}
                className="rounded-2xl border border-border/70 bg-card/90 p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      {row.icon}
                    </div>
                    <div className="min-w-0 space-y-1">
                      <p className="truncate text-sm font-semibold text-foreground">{row.name}</p>
                      {row.href ? (
                        <ExternalLinkText href={row.href} truncate>
                          {row.subtitle}
                        </ExternalLinkText>
                      ) : (
                        <p className="truncate text-sm text-muted-foreground">{row.subtitle}</p>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline">{row.typeLabel}</Badge>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      <FormattedMessage
                        id="brandSourceMaterials.lastScanned"
                        defaultMessage="Last scanned"
                      />
                    </p>
                    <p className="mt-1 text-muted-foreground">{formatDate(row.lastScannedAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      <FormattedMessage id="brandSourceMaterials.added" defaultMessage="Added" />
                    </p>
                    <p className="mt-1 text-muted-foreground">{formatDate(row.addedAt)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden overflow-hidden rounded-2xl border border-border/70 bg-card/90 shadow-sm md:block">
            <div className="overflow-x-auto">
              <div className="min-w-[760px]">
                <div className="grid grid-cols-[minmax(0,1.8fr)_120px_140px_140px] gap-4 border-b border-border/70 bg-muted/30 px-5 py-3 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  <div>
                    <FormattedMessage id="brandSourceMaterials.name" defaultMessage="Name" />
                  </div>
                  <div>
                    <FormattedMessage id="brandSourceMaterials.type" defaultMessage="Type" />
                  </div>
                  <div>
                    <FormattedMessage
                      id="brandSourceMaterials.lastScanned"
                      defaultMessage="Last scanned"
                    />
                  </div>
                  <div>
                    <FormattedMessage id="brandSourceMaterials.added" defaultMessage="Added" />
                  </div>
                </div>

                {rows.map((row) => (
                  <div
                    key={row.id}
                    className="grid grid-cols-[minmax(0,1.8fr)_120px_140px_140px] gap-4 border-b border-border/70 px-5 py-4 last:border-b-0"
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        {row.icon}
                      </div>
                      <div className="min-w-0 space-y-1">
                        <p className="truncate text-sm font-semibold text-foreground">{row.name}</p>
                        {row.href ? (
                          <ExternalLinkText href={row.href} truncate>
                            {row.subtitle}
                          </ExternalLinkText>
                        ) : (
                          <p className="truncate text-sm text-muted-foreground">{row.subtitle}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center">
                      <Badge variant="outline">{row.typeLabel}</Badge>
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground">
                      {formatDate(row.lastScannedAt)}
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground">
                      {formatDate(row.addedAt)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
